import LegalLayout, { Section } from "@/components/site/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      updated="December 2025"
      seo={{
        title: "Privacy Policy | Xadentra",
        description: "How Xadentra collects, uses, stores, and protects your personal information when you interact with our website and services.",
        path: "/privacy-policy",
      }}
    >
      <p className="text-base text-[#07111F]">
        This Privacy Policy explains how Xadentra Inc. ("Xadentra", "we", "our", or "us") collects,
        uses, discloses, and safeguards information when you visit our website, submit a form, or
        engage our services. By using the site you agree to the practices described here.
      </p>

      <Section title="1. Information we collect">
        <p>We collect information you provide directly (such as name, business email, company, job title, phone number, country, and details about your inquiry), information collected automatically (such as IP address, device information, pages visited, referrer, and approximate location), and information from third parties (such as enrichment providers and advertising platforms, only where lawful).</p>
      </Section>

      <Section title="2. How form submissions are used">
        <p>Form submissions are used to respond to your inquiry, schedule strategy calls, send relevant resources, and improve our services. We may store submitted data in our CRM, marketing automation platform, and analytics tools to maintain a record of the engagement.</p>
      </Section>

      <Section title="3. Analytics and tracking (consent-gated)">
        <p>
          We use <strong className="text-[#07111F]">PostHog</strong> for anonymous product analytics
          so we can understand which pages and content help visitors. PostHog and any future analytics
          or marketing tools <strong className="text-[#07111F]">only load after you grant the matching
          category of consent</strong> through our cookie banner. If you select &quot;Reject all&quot;,
          no non-essential analytics or marketing scripts are loaded. See our{" "}
          <a href="/cookie-policy" className="text-[#6D4DFF] underline">Cookie Policy</a> for details
          and how to change your choice anytime.
        </p>
      </Section>

      <Section title="4. CRM and marketing automation">
        <p>
          When you submit a form or email us, the content of your message and the contact details you
          provide may be stored in our customer-relationship and marketing-automation tooling
          (including Salesforce and Adobe Marketo Engage) so we can respond, schedule a working
          session, and maintain a record of the engagement. You can opt out of marketing
          communications at any time using the unsubscribe link in any email or by emailing us.
        </p>
      </Section>

      <Section title="5. Legal bases (EEA / UK)">
        <p>For visitors in the EEA or UK, we process personal data based on legitimate interests (responding to inquiries, securing the site), consent (analytics and marketing cookies, marketing emails), and contract performance (delivering engaged services).</p>
      </Section>

      <Section title="6. Data retention">
        <p>We retain submitted information for as long as necessary to fulfil the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce agreements. Inquiry records are typically retained for up to 36 months after the last interaction.</p>
      </Section>

      <Section title="7. Sharing">
        <p>We do not sell personal information. We share data only with service providers under contract (such as hosting, analytics, CRM, marketing automation, and email infrastructure), or where required by law.</p>
      </Section>

      <Section title="8. Your rights">
        <p>
          <strong className="text-[#07111F]">EEA / UK (GDPR).</strong> You may request access,
          correction, deletion, restriction, or portability of your personal data, withdraw any
          consent you previously granted, or object to certain processing.
        </p>
        <p className="mt-3">
          <strong className="text-[#07111F]">California &amp; other US states (CCPA/CPRA).</strong>{" "}
          You may request what personal information we have collected about you, request deletion or
          correction, opt out of any &quot;sale&quot; or &quot;sharing&quot; of personal information,
          and limit the use of sensitive personal information. We do not sell or share personal
          information for cross-context behavioral advertising.
        </p>
        <p className="mt-3">
          To exercise any of these rights, email{" "}
          <a href={"mail" + "to:" + "contact" + "@" + "xadentra.com"} className="text-[#6D4DFF] underline">{"contact" + "@" + "xadentra.com"}</a>.
          We will respond within the timelines required by applicable law.
        </p>
      </Section>

      <Section title="9. Security">
        <p>We use commercially reasonable safeguards to protect personal information. No system is fully secure; if you believe your information has been compromised, please contact us immediately.</p>
      </Section>

      <Section title="10. Children">
        <p>Our site and services are not directed at individuals under 16. We do not knowingly collect data from children.</p>
      </Section>

      <Section title="11. Updates to this policy">
        <p>We may update this Policy from time to time. Material changes will be posted on this page with a new "Last updated" date.</p>
      </Section>

      <Section title="12. Contact">
        <p>Questions about this Policy or your data can be sent to <a href={"mail" + "to:" + "contact" + "@" + "xadentra.com"} className="text-[#6D4DFF] underline">{"contact" + "@" + "xadentra.com"}</a>.</p>
      </Section>
    </LegalLayout>
  );
}
